import { DeliveryCard, DispatchCard } from 'src/entities/dispatch';

import { VehicleRowGroup } from './adapter';

/**
 * D&D で楽観的に置いた override カードを、サーバ由来の行グループ (serverRowGroups) へ被せる。
 *
 * override は rowId 単位。override が無い行では、override で確定した legRunId を含む
 * 固定ルート由来の仮カード (fr-template) のうち降地側だけを隠す (hidePlacedFrTemplate)。
 * 積地は複数 truck が同じ legRun を候補に持てるため対象外にする。
 *
 * 変化しない行・グループは元のオブジェクトをそのまま返す。override が 1 件でもあると
 * 毎回全グループを spread コピーしていたため、カードを 1 枚動かすだけで無関係な車両の
 * 行まで参照が変わり memo が効かなくなっていた（hideDeletingShifts / mergeExtraRotations
 * と同じ規約に揃える）。
 */
export function applyOverrideRowCards(
  serverRowGroups: VehicleRowGroup[],
  overrideRowCards: ReadonlyMap<string, DispatchCard[]>,
): VehicleRowGroup[] {
  if (overrideRowCards.size === 0) return serverRowGroups;

  // override で配置された legRunId を収集
  // → 他の行の仮カード (fr-template) でこの legRun を含むものは非表示にする
  // （固定ルート外や別車両に落とされたら、元の固定ルート位置の仮カードは消える）
  const placedLegRunIds = new Set<number>();
  for (const cards of overrideRowCards.values()) {
    for (const c of cards) {
      if (c.type !== 'delivery') continue;
      (c.legRunIds ?? []).forEach((id) => placedLegRunIds.add(id));
    }
  }

  const hidePlacedFrTemplate = (cards: DispatchCard[]): DispatchCard[] => {
    if (placedLegRunIds.size === 0) return cards;
    const filtered = cards.filter((c) => {
      if (c.type !== 'delivery' || c.status !== 'fr-template') return true;
      // 積地 (frStopType='loading') は複数 truck が同じ積地で legRun を候補に持つことがあり、
      // 片方の row に legRun が確定しても他 row の積地候補は残るべき。フィルタ対象は降地のみ。
      if (c.frStopType === 'loading') return true;
      return !(c.legRunIds ?? []).some((id) => placedLegRunIds.has(id));
    });
    // Array.prototype.filter は取り除く要素が無くても新しい配列を返す。ここで長さ比較を
    // 挟まないと、legRunIds を持つ override が 1 件でもあるだけで（= 通常の D&D で毎回）
    // 何も隠さない行まで新しい配列になり、下の rowsChanged 判定が常に真になって
    // この関数の目的（無関係な行・グループの参照維持）が働かなくなる。
    return filtered.length === cards.length ? cards : filtered;
  };

  let changed = false;
  const next = serverRowGroups.map((group) => {
    let rowsChanged = false;
    const newRows = group.rows.map((row) => {
      const override = overrideRowCards.get(row.rowId);
      if (override) {
        rowsChanged = true;
        return {
          ...row,
          cards: override,
          deliveryCards: override.filter(
            (c): c is DeliveryCard => c.type === 'delivery' && c.status !== 'no-delivery' && c.status !== 'fr-template',
          ),
        };
      }
      const filtered = hidePlacedFrTemplate(row.cards);
      if (filtered === row.cards) return row;
      rowsChanged = true;
      return {
        ...row,
        cards: filtered,
        deliveryCards: filtered.filter(
          (c): c is DeliveryCard => c.type === 'delivery' && c.status !== 'no-delivery' && c.status !== 'fr-template',
        ),
      };
    });
    // 参照が変わると memo 済みの行・グループが全部描き直される
    // （hideDeletingShifts と同じ規約）
    if (!rowsChanged) return group;
    changed = true;
    const allCards = newRows.flatMap((r) => r.cards);
    return { ...group, rows: newRows, vehicle: { ...group.vehicle, cards: allCards } };
  });
  return changed ? next : serverRowGroups;
}
